import React, { useEffect, useMemo, useState } from "react";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Plus, Coins, CreditCard, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import CustomerCombobox from "@/components/CustomerCombobox";

export default function Schemes() {
  const [schemes, setSchemes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [openScheme, setOpenScheme] = useState(false);
  const [openAcc, setOpenAcc] = useState(false);
  const [openInst, setOpenInst] = useState(null);
  const [schemeForm, setSchemeForm] = useState({
    name: "",
    scheme_type: "monthly",
    duration_months: 11,
    monthly_amount: 1000,
    benefit_description: "1 month bonus on maturity",
  });
  const [accForm, setAccForm] = useState({
    customer_id: "",
    scheme_id: "",
    start_date: new Date().toISOString().slice(0, 10),
  });
  const [instForm, setInstForm] = useState({
    amount: 0,
    mode: "cash",
    notes: "",
  });

  const load = async () => {
    const [s, a, c] = await Promise.all([
      api.get("/schemes"),
      api.get("/scheme-accounts"),
      api.get("/customers"),
    ]);
    setSchemes(s.data);
    setAccounts(a.data);
    setCustomers(c.data);
  };
  useEffect(() => {
    load();
  }, []);

  const saveScheme = async () => {
    if (!schemeForm.name.trim()) {
      toast.error("Name required");
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      await api.post("/schemes", {
        ...schemeForm,
        duration_months: Number(schemeForm.duration_months),
        monthly_amount: Number(schemeForm.monthly_amount),
      });
      toast.success("Scheme created");
      setOpenScheme(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const saveAccount = async () => {
    if (!accForm.customer_id || !accForm.scheme_id) {
      toast.error("Select customer & scheme");
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      await api.post("/scheme-accounts", accForm);
      toast.success("Account opened");
      setOpenAcc(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const payInstallment = async () => {
    if (!Number(instForm.amount)) {
      toast.error("Amount required");
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      await api.post("/scheme-installments", {
        account_id: openInst.id,
        amount: Number(instForm.amount),
        mode: instForm.mode,
        notes: instForm.notes,
      });
      toast.success("Installment paid");
      setOpenInst(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const removeScheme = async (s) => {
    if (!window.confirm(`Delete scheme ${s.name}?`)) return;
    await api.delete(`/schemes/${s.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="schemes-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Customer Retention
        </div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">
          Gold Saving Schemes
        </h1>
      </header>

      <Tabs defaultValue="schemes" className="space-y-6">
        <TabsList className="bg-secondary">
          <TabsTrigger value="schemes" data-testid="tab-schemes">
            Schemes
          </TabsTrigger>
          <TabsTrigger value="accounts" data-testid="tab-scheme-accounts">
            Customer Accounts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="schemes" className="space-y-4">
          <div className="flex justify-end">
            <Button
              data-testid="add-scheme-btn"
              onClick={() => setOpenScheme(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus size={16} className="mr-1.5" /> New scheme
            </Button>
          </div>
          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/40 hover:bg-secondary/40">
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                  <TableHead className="text-right">Monthly</TableHead>
                  <TableHead>Benefit</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schemes.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-10 text-muted-foreground"
                    >
                      No schemes yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  schemes.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Coins
                            className="text-gold"
                            size={16}
                            weight="duotone"
                          />{" "}
                          {s.name}
                        </div>
                      </TableCell>
                      <TableCell className="capitalize text-sm">
                        {s.scheme_type}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {s.duration_months} mo
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {inr(s.monthly_amount)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {s.benefit_description}
                      </TableCell>
                      <TableCell className="text-right">
                        <button
                          onClick={() => removeScheme(s)}
                          className="p-1.5 rounded-md hover:bg-destructive/15 text-muted-foreground hover:text-destructive"
                        >
                          <Trash size={14} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="accounts" className="space-y-4">
          <div className="flex justify-end">
            <Button
              data-testid="open-account-btn"
              onClick={() => setOpenAcc(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus size={16} className="mr-1.5" /> Open account
            </Button>
          </div>
          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/40 hover:bg-secondary/40">
                  <TableHead>Account</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Scheme</TableHead>
                  <TableHead className="text-right">Paid / Due</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-10 text-muted-foreground"
                    >
                      No accounts yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="font-mono text-xs">
                        {a.account_no}
                      </TableCell>
                      <TableCell>{a.customer_name}</TableCell>
                      <TableCell>{a.scheme_name}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {a.installments_paid}/{a.duration_months}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {inr(a.total_paid)}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`text-xs uppercase tracking-wider ${a.status === "matured" ? "text-gold" : "text-muted-foreground"}`}
                        >
                          {a.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {a.status !== "matured" && (
                          <Button
                            data-testid={`pay-inst-${a.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setInstForm({
                                amount: a.monthly_amount,
                                mode: "cash",
                                notes: "",
                              });
                              setOpenInst(a);
                            }}
                            className="text-gold"
                          >
                            <CreditCard size={14} className="mr-1" /> Pay
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* New scheme dialog */}
      <Dialog open={openScheme} onOpenChange={setOpenScheme}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              New Scheme
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field
              label="Name *"
              value={schemeForm.name}
              onChange={(v) => setSchemeForm({ ...schemeForm, name: v })}
            />
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Type
              </Label>
              <Select
                value={schemeForm.scheme_type}
                onValueChange={(v) =>
                  setSchemeForm({ ...schemeForm, scheme_type: v })
                }
              >
                <SelectTrigger className="mt-1.5 bg-background border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="festival">Festival</SelectItem>
                  <SelectItem value="accumulation">Accumulation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field
              label="Duration (months)"
              type="number"
              value={schemeForm.duration_months}
              onChange={(v) =>
                setSchemeForm({ ...schemeForm, duration_months: v })
              }
            />
            <Field
              label="Monthly amount"
              type="number"
              value={schemeForm.monthly_amount}
              onChange={(v) =>
                setSchemeForm({ ...schemeForm, monthly_amount: v })
              }
            />
            <Field
              label="Benefit"
              value={schemeForm.benefit_description}
              onChange={(v) =>
                setSchemeForm({ ...schemeForm, benefit_description: v })
              }
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenScheme(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-scheme-btn"
              onClick={saveScheme}
              loading={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New account dialog */}
      <Dialog open={openAcc} onOpenChange={setOpenAcc}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Open Customer Account
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Customer
              </Label>
              <div className="mt-1.5">
                <CustomerCombobox
                  value={
                    customers.find((c) => c.id === accForm.customer_id) || null
                  }
                  onChange={(c) =>
                    setAccForm({ ...accForm, customer_id: c?.id || "" })
                  }
                  onCreate={(c) => setCustomers((arr) => [c, ...arr])}
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Scheme
              </Label>
              <Select
                value={accForm.scheme_id}
                onValueChange={(v) => setAccForm({ ...accForm, scheme_id: v })}
              >
                <SelectTrigger
                  data-testid="acc-scheme"
                  className="mt-1.5 bg-background border-border"
                >
                  <SelectValue placeholder="Choose" />
                </SelectTrigger>
                <SelectContent>
                  {schemes.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} · {inr(s.monthly_amount)}/mo
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Field
              label="Start date"
              type="date"
              value={accForm.start_date}
              onChange={(v) => setAccForm({ ...accForm, start_date: v })}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenAcc(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-account-btn"
              onClick={saveAccount}
              loading={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Open
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay installment */}
      <Dialog open={!!openInst} onOpenChange={(v) => !v && setOpenInst(null)}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Pay Installment
            </DialogTitle>
          </DialogHeader>
          {openInst && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                {openInst.account_no} · {openInst.customer_name}
              </div>
              <Field
                label="Amount"
                type="number"
                value={instForm.amount}
                onChange={(v) => setInstForm({ ...instForm, amount: v })}
              />
              <div>
                <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                  Payment mode
                </Label>
                <Select
                  value={instForm.mode}
                  onValueChange={(v) => setInstForm({ ...instForm, mode: v })}
                >
                  <SelectTrigger className="mt-1.5 bg-background border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["cash", "upi", "card", "bank"].map((m) => (
                      <SelectItem key={m} value={m}>
                        {m.toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Field
                label="Notes"
                value={instForm.notes}
                onChange={(v) => setInstForm({ ...instForm, notes: v })}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenInst(null)}>
              Cancel
            </Button>
            <Button
              data-testid="confirm-inst-btn"
              onClick={payInstallment}
              loading={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Receive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text" }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
      {label}
    </Label>
    <Input
      type={type}
      className="mt-1.5 bg-background border-border"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);
